import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { qcommerceApi } from '../../services/qcommerceApi';
import PlatformLogo from '../../components/ui/PlatformLogo';
import { QC_PLATFORM_IDS } from '../../constants/qcommerce';
import { getPlatform } from '../../constants/platforms';

function openQcStore(navigate, store) {
  const { platform, id } = store;
  if (platform === 'zepto') {
    navigate(`/qc/zepto/brands/${id}`);
    return;
  }
  navigate(`/qc/${platform}/brands/${id}/ads/summary`);
}

function teamLabel(store) {
  const ownerNames = (store.owners || []).map((o) => o.name).filter(Boolean);
  const assigned = (store.assignedMembers || []).map((m) => m.email).filter(Boolean);
  if (ownerNames.length) return ownerNames.join(', ');
  if (assigned.length) return assigned.join(', ');
  return '—';
}

async function loadStoresViaPlatforms() {
  const stores = [];
  for (const platform of QC_PLATFORM_IDS) {
    try {
      const res = await qcommerceApi.listBrands(platform);
      if (!res.data?.success) continue;
      for (const b of res.data.brands || []) {
        stores.push({
          id: b.id,
          name: b.name,
          platform,
          platformName: getPlatform(platform).name,
          currency: b.currency || 'INR',
          owners: [],
          assignedMembers: [],
          isDemo: Boolean(b.isDemo),
        });
      }
    } catch {
      /* try other platforms */
    }
  }
  stores.sort((a, b) => {
    const pc = a.platform.localeCompare(b.platform);
    return pc !== 0 ? pc : a.name.localeCompare(b.name);
  });
  return stores;
}

export default function QCommerceStoresHubList({
  isDarkMode,
  searchQuery = '',
  onStoreCount,
}) {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      try {
        const res = await qcommerceApi.listHubStores();
        if (res.data?.success && (res.data.stores?.length || 0) > 0) {
          setStores(res.data.stores);
          onStoreCount?.(res.data.count ?? res.data.stores.length);
          return;
        }
      } catch {
        /* use per-platform list */
      }
      const list = await loadStoresViaPlatforms();
      setStores(list);
      onStoreCount?.(list.length);
      if (!list.length) {
        toast.error('No Quick Commerce stores found. Add a brand or ask admin to run QC data migration.');
      }
    } catch {
      toast.error('Could not load Quick Commerce stores');
    } finally {
      setLoading(false);
    }
  }, [onStoreCount]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) => {
      const hay = [
        s.name,
        s.platformName,
        s.platform,
        teamLabel(s),
        ...(s.owners || []).map((o) => o.email),
        ...(s.assignedMembers || []).map((m) => m.email),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [stores, searchQuery]);

  const text = isDarkMode ? 'text-white' : 'text-slate-900';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-600';
  const border = isDarkMode ? 'border-slate-800' : 'border-slate-200';
  const rowHover = isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50';

  const adminTeamPath = '/user-management?tab=quick_commerce';

  return (
    <div className="space-y-4">
      <div
        className={`rounded-xl border p-4 ${
          isDarkMode ? 'border-orange-500/30 bg-orange-500/5' : 'border-orange-200 bg-orange-50/80'
        }`}
      >
        <h2 className={`text-lg font-bold ${text}`}>Quick Commerce stores</h2>
        <p className={`text-sm mt-1 max-w-2xl ${muted}`}>
          Blinkit, Zepto, and Instamart — separate from Amazon. Use <strong>Open</strong> like Amazon stores. Assign
          team access from the sidebar (User management) or{' '}
          <Link to={adminTeamPath} className="text-orange-700 font-medium hover:underline">
            Team QC access
          </Link>
          .
        </p>
      </div>

      <div className={`rounded-xl border overflow-hidden ${border} ${isDarkMode ? 'bg-slate-900/30' : 'bg-white'}`}>
        <div
          className={`hidden lg:grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] gap-3 px-4 py-2.5 text-[10px] font-bold uppercase tracking-wider ${
            isDarkMode ? 'bg-slate-800/80 text-slate-400' : 'bg-slate-50 text-slate-500'
          }`}
        >
          <span>Store</span>
          <span>Marketplace</span>
          <span>Owner / team</span>
          <span className="text-right">Action</span>
        </div>

        {loading ? (
          <p className={`p-8 text-sm ${muted}`}>Loading Quick Commerce stores…</p>
        ) : filtered.length === 0 ? (
          <p className={`p-8 text-sm ${muted}`}>
            {stores.length === 0
              ? 'No Quick Commerce stores yet. Ask an admin to add a brand or load demo data from a Zepto workspace.'
              : 'No stores match your search.'}
          </p>
        ) : (
          <ul>
            {filtered.map((store) => (
              <li
                key={`${store.platform}-${store.id}`}
                className={`border-t ${border} ${rowHover} transition-colors`}
              >
                <div className="lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.2fr)_auto] lg:gap-3 lg:items-center px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white border border-slate-100 shrink-0">
                      <PlatformLogo platformId={store.platform} size={22} />
                    </div>
                    <div className="min-w-0">
                      <div className={`font-semibold truncate ${text}`}>{store.name}</div>
                      <div className={`text-xs truncate ${muted}`}>
                        {store.isDemo ? 'Demo · ' : ''}
                        {store.currency || 'INR'}
                      </div>
                    </div>
                  </div>

                  <div className={`text-sm mt-2 lg:mt-0 ${muted}`}>{store.platformName}</div>

                  <div className={`text-sm mt-1 lg:mt-0 ${muted} truncate`} title={teamLabel(store)}>
                    {teamLabel(store)}
                  </div>

                  <div className="mt-3 lg:mt-0 flex justify-end">
                    <button
                      type="button"
                      onClick={() => openQcStore(navigate, store)}
                      className="inline-flex items-center gap-1 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 shadow-sm"
                    >
                      Open
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!loading && filtered.length > 0 ? (
        <p className={`text-xs ${muted}`}>
          {filtered.length} Quick Commerce store{filtered.length === 1 ? '' : 's'}
        </p>
      ) : null}
    </div>
  );
}
